import React, { useState, useEffect } from 'react';

const AdminBookings = () => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [showDetails, setShowDetails] = useState(null);

    const API_URL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:3001').replace(/\/$/, '');

    const getAuthHeaders = React.useCallback(() => {
        const token = localStorage.getItem('adminToken');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    }, []);

    const loadBookings = React.useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/api/bookings`, {
                headers: getAuthHeaders()
            });
            const data = await response.json();
            setBookings(data);
        } catch (error) {
            console.error('Error loading bookings:', error);
        } finally {
            setLoading(false);
        }
    }, [API_URL, getAuthHeaders]);

    useEffect(() => {
        loadBookings();
    }, [loadBookings]);

    const updateStatus = async (id, status) => {
        try {
            const response = await fetch(`${API_URL}/api/bookings/${id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ status })
            });

            if (response.ok) {
                loadBookings();
                alert('Booking status updated!');
            }
        } catch (error) {
            console.error('Error updating booking:', error);
            alert('Error updating booking');
        }
    };

    const deleteBooking = async (id) => {
        if (!window.confirm('Are you sure you want to delete this booking?')) return;

        try {
            const response = await fetch(`${API_URL}/api/bookings/${id}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (response.ok) {
                loadBookings();
                alert('Booking deleted!');
            }
        } catch (error) {
            console.error('Error deleting booking:', error);
            alert('Error deleting booking');
        }
    };

    const filteredBookings = bookings.filter(booking => {
        if (filter === 'all') return true;
        return booking.status === filter;
    });

    const formatBookingDate = (booking) => {
        const raw = booking.startDate ?? booking.bookingDate;
        if (!raw) return '—';
        const d = new Date(raw);
        return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
    };

    const getTravelers = (booking) => booking.travelers ?? booking.numberOfTravelers ?? '—';

    const getStatusColor = (status) => {
        switch (status) {
            case 'confirmed': return 'bg-green-100 text-green-800';
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'cancelled': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return <div className="text-center py-8">Loading...</div>;
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold">Bookings Management</h2>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setFilter('all')}
                        className={`px-4 py-2 rounded ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                    >
                        All ({bookings.length})
                    </button>
                    <button
                        onClick={() => setFilter('pending')}
                        className={`px-4 py-2 rounded ${filter === 'pending' ? 'bg-yellow-600 text-white' : 'bg-gray-200'}`}
                    >
                        Pending ({bookings.filter(b => b.status === 'pending').length})
                    </button>
                    <button
                        onClick={() => setFilter('confirmed')}
                        className={`px-4 py-2 rounded ${filter === 'confirmed' ? 'bg-green-600 text-white' : 'bg-gray-200'}`}
                    >
                        Confirmed ({bookings.filter(b => b.status === 'confirmed').length})
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tour</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Travelers</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredBookings.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                                    No bookings found
                                </td>
                            </tr>
                        ) : (
                            filteredBookings.map(booking => (
                                <tr key={booking.id}>
                                    <td className="px-6 py-4">
                                        <div>
                                            <div className="font-semibold">{booking.customer.name}</div>
                                            <div className="text-sm text-gray-500">{booking.customer.email}</div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">{booking.tourName}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {formatBookingDate(booking)}
                                    </td>
                                    <td className="px-6 py-4">{getTravelers(booking)}</td>
                                    <td className="px-6 py-4">₹{booking.totalPrice}</td>
                                    <td className="px-6 py-4">
                                        <select
                                            value={booking.status}
                                            onChange={(e) => updateStatus(booking.id, e.target.value)}
                                            className={`px-2 py-1 rounded ${getStatusColor(booking.status)}`}
                                        >
                                            <option value="pending">Pending</option>
                                            <option value="confirmed">Confirmed</option>
                                            <option value="cancelled">Cancelled</option>
                                        </select>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <button
                                            onClick={() => setShowDetails(booking)}
                                            className="text-blue-600 hover:text-blue-800 mr-3"
                                            title="View Details"
                                        >
                                            <i className="fas fa-eye"></i>
                                        </button>
                                        <button
                                            onClick={() => deleteBooking(booking.id)}
                                            className="text-red-600 hover:text-red-800"
                                            title="Delete"
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Details Modal */}
            {showDetails && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-2xl font-bold">Booking Details</h3>
                            <button onClick={() => setShowDetails(null)} className="text-gray-500 hover:text-gray-700">
                                <i className="fas fa-times text-2xl"></i>
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="font-semibold">Customer Name:</label>
                                    <p>{showDetails.customer.name}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Email:</label>
                                    <p>{showDetails.customer.email}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Phone:</label>
                                    <p>{showDetails.customer.phone}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Tour:</label>
                                    <p>{showDetails.tourName}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Booking Date:</label>
                                    <p>{formatBookingDate(showDetails)}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Travelers:</label>
                                    <p>{getTravelers(showDetails)}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Total Price:</label>
                                    <p className="text-lg font-bold">₹{showDetails.totalPrice}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Status:</label>
                                    <p className={`inline-block px-2 py-1 rounded ${getStatusColor(showDetails.status)}`}>
                                        {showDetails.status}
                                    </p>
                                </div>
                            </div>
                            {showDetails.specialRequests && (
                                <div>
                                    <label className="font-semibold">Special Requests:</label>
                                    <p className="mt-1 p-3 bg-gray-50 rounded">{showDetails.specialRequests}</p>
                                </div>
                            )}
                        </div>
                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={() => setShowDetails(null)}
                                className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminBookings;
